import type { ExecutionLock, PilotRecord, PilotDraftSync } from "@/lib/pilot";
import axios from "axios";
import { apiClient } from "./client";

// The Email module, under each project (`/projects/:slug/email/*`): a campaign
// belongs to one CFO or M&A project and every call is checked against the
// caller's role there. Types mirror prism-backend/src/email_campaigns/email_schema.py.

export type CampaignStatus = "DRAFT" | "ACTIVE" | "PAUSED" | "ARCHIVED";
export type ContactEmailStatus =
  | "EMAIL_READY"
  | "EMAIL_QUEUED"
  | "EMAIL_ACTIVE"
  | "EMAIL_PAUSED"
  | "EMAIL_REPLIED"
  | "EMAIL_BOUNCED"
  | "EMAIL_COMPLETED"
  | "EMAIL_REMOVED"
  | "EMAIL_UNSUBSCRIBED";
export type CompanyEmailStatus =
  | "EMAIL_READY"
  | "EMAIL_IN_PROGRESS"
  | "EMAIL_PAUSED"
  | "EMAIL_REPLIED"
  | "EMAIL_BOUNCED"
  | "EMAIL_UNSUBSCRIBED"
  | "EMAIL_COMPLETED";
export type DelayUnit = "DAYS" | "WEEKS";
export type EmailScope = "CFO" | "MA";
export type ProviderMode = "NONE" | "DRY_RUN" | "LIVE";
export type Eligibility =
  | "READY"
  | "NO_EMAIL"
  | "ALREADY_IN_CAMPAIGN"
  | "IN_OTHER_CAMPAIGN"
  | "LEGACY_ENROLLED"
  | "UNSUBSCRIBED"
  | "BOUNCED"
  | "INVALID_EMAIL";
export type PlannedAction = "ADD" | "MOVE" | "SKIP";
/** An email's furthest state; QUEUED = handed to Lemlist, not sent yet. */
export type MessageStatus = "QUEUED" | "SENT" | "DELIVERED" | "OPENED" | "CLICKED" | "REPLIED" | "BOUNCED";
/** RENDERED = Prism's frozen render is the content; a reply's content is
 *  PENDING (read at the next sync), AVAILABLE, or UNAVAILABLE from Lemlist. */
export type ContentState = "RENDERED" | "PENDING" | "AVAILABLE" | "UNAVAILABLE";

// ── meta ─────────────────────────────────────────────────────────────────

/** The current project and what the user may do in its Email module. */
export interface EmailProjectAccess {
  id: number;
  slug: string;
  name: string;
  project_type: string;
  /** The M&A / CFO badge, derived from the project's type. */
  scope: EmailScope;
  can_manage: boolean;
  can_enroll: boolean;
  can_pause: boolean;
  can_remove: boolean;
  /** Sync with Lemlist, and start / pause / archive a campaign there. */
  can_sync: boolean;
}

export interface EmailVariable {
  token: string;
  label: string;
  description: string;
  namespace: string;
  example: string | null;
}

export interface BusinessStatus {
  key: string;
  label: string;
  color: string | null;
}

/** What a project's Email page needs first: everything is this project's. */
export interface EmailMeta {
  project: EmailProjectAccess;
  provider: string;
  /** False = dry run: nothing reaches Lemlist. */
  external_effects: boolean;
  /** The variables this project's templates may use. */
  variables: EmailVariable[];
  /** The project's pipeline statuses, in order. */
  business_statuses: BusinessStatus[];
}

// ── campaigns & steps ────────────────────────────────────────────────────

/** Who sends a campaign: a Lemlist sender of the team and its mailbox. */
export interface EmailSender {
  user_id: string;
  email: string | null;
}

export interface EmailSenderOption extends EmailSender {
  /** How many Lemlist campaigns this sender already sends. */
  campaigns: number;
}

export interface EmailSenders {
  /** False in a dry run: Lemlist is not asked, nobody to pick. */
  external: boolean;
  senders: EmailSenderOption[];
  note: string | null;
}

export interface CampaignSettings {
  timezone: string;
  sending_days: number[];
  sending_start_time: string;
  sending_end_time: string;
  daily_limit: number;
  default_delay_days: number;
  /** null: Lemlist's default (the team's only sender), set at the first sync. */
  sender?: EmailSender | null;
  provider_settings: Record<string, unknown>;
}

export interface AiVariable {
  id: number;
  key: string;
  prompt_template: string;
  model: string | null;
}

export interface CampaignStep {
  id: number;
  position: number;
  name: string;
  type: string;
  delay_value: number;
  delay_unit: DelayUnit;
  subject_template: string;
  body_template: string;
  provider_step_id: string | null;
  version: number;
  content_hash: string;
  /** The provider does not have this content yet. */
  unsynced: boolean;
  variables_count: number;
  ai_variables: AiVariable[];
  /** On a campaign's detail: what can happen to this step now. */
  capabilities?: StepCapabilities | null;
}

/** What can happen to one step now. */
export interface StepCapabilities {
  can_delete: boolean;
  delete_reason: string | null;
  /** Lemlist skips a step in its app only: never offered through Prism. */
  can_skip: boolean;
  skip_reason: string | null;
  /** Contacts this step was sent to. */
  reached: number;
  /** Added once contacts were already in the campaign. */
  added_after_launch: boolean;
  /** Not in Lemlist's sequence yet: the next Sync adds it there. */
  pending: boolean;
}

export type SequenceCapability =
  | "can_edit_step_content"
  | "can_edit_step_delay"
  | "can_add_step"
  | "can_insert_step"
  | "can_reorder_steps";

/** What can change in a campaign's sequence now. Everything until Lemlist holds
 *  contacts; then content, delays and a step at the end. */
export type SequenceCapabilities = Record<SequenceCapability, boolean> & {
  /** Lemlist holds contacts of this campaign. */
  active_sequence: boolean;
  /** Capability → why it is off. */
  reasons: Partial<Record<SequenceCapability, string>>;
  message: string | null;
};

/** Who a step added at `position` reaches — never retroactively. */
export interface StepImpact {
  position: number;
  mode: "APPEND" | "INSERT";
  allowed: boolean;
  reason: string | null;
  total: number;
  will_receive: number;
  already_past: number;
  completed: number;
  paused: number;
  replied: number;
  removed: number;
  /** Bounced or unsubscribed. */
  ended: number;
}

export type EmailMetrics = Record<"sent" | "opened" | "clicked" | "replied" | "bounced", number>;

export interface Campaign {
  id: number;
  project_id: number;
  project_slug: string;
  project_name: string;
  scope: EmailScope;
  name: string;
  provider: string;
  provider_campaign_id: string | null;
  provider_mode: ProviderMode;
  status: CampaignStatus;
  settings: CampaignSettings;
  steps_count: number;
  /** Live memberships: ready, queued, in sequence, paused. */
  contacts_count: number;
  contacts_by_status: Record<string, number>;
  last_synced_at: string | null;
  sync_error: string | null;
  unsynced_changes: boolean;
  /** From Prism's synced messages: sent, opened, clicked, replied, bounced. */
  metrics: EmailMetrics;
  /** The Email Sync job's reconciliation with Lemlist. */
  provider_status: string | null;
  last_provider_sync_at: string | null;
  last_provider_error: string | null;
  /** Lives in a previous Lemlist team: Prism keeps its history, read-only. */
  provider_legacy: boolean;
  /** A locked pilot: shown for review; only dedicated draft synchronization is allowed. */
  execution_lock?: ExecutionLock | null;
  /** Unique leads, from Prism's messages (the Performance definitions). */
  lead_stats?: CampaignLeadStats | null;
  created_at: string;
  updated_at: string;
}

/** Unique leads of a campaign (never messages): launched to Lemlist, reached
 *  by at least one email, and having replied at least once. */
export interface CampaignLeadStats {
  launched: number;
  reached: number;
  replied: number;
}

export interface CampaignDetail extends Campaign {
  steps: CampaignStep[];
  sequence: SequenceCapabilities;
  /** Step position → running contacts last sent that step. */
  contacts_by_step: Record<string, number>;
  /** Step position → what its emails did, from Prism's synced messages
   *  (a reply counts for the step it answers). */
  metrics_by_step?: Record<string, EmailMetrics>;
  /** A locked pilot's review record (A/B plan, cadence, annotations, decisions). */
  pilot?: PilotRecord | null;
  pilot_sync?: PilotDraftSync | null;
}

export interface CampaignCreate {
  name: string;
  settings?: Partial<CampaignSettings>;
}

export interface CampaignUpdate {
  name?: string;
  settings?: Partial<CampaignSettings>;
  status?: CampaignStatus;
}

export interface StepInput {
  name: string;
  delay_value?: number | null;
  delay_unit?: DelayUnit;
  subject_template?: string;
  body_template?: string;
  position?: number | null;
}

export type StepUpdate = Partial<Omit<StepInput, "position">>;

export interface AiVariableInput {
  key: string;
  prompt_template: string;
  model?: string | null;
}

// ── preview & AI test ────────────────────────────────────────────────────

export interface RenderErrorRead {
  kind: string;
  token: string | null;
  message: string;
}

export interface ResolvedVariable {
  token: string;
  value: string | null;
  source: "contact" | "company" | "ai";
  /** The template's fallback, used because the value is missing. */
  fallback?: string | null;
}

/** How often each variable has a value among the contacts a campaign reaches. */
export interface VariableFill {
  project_id: number;
  /** "campaign": its live contacts; "project": contacts with an email (no contact added yet). */
  population: "campaign" | "project";
  total: number;
  /** Contacts actually read: all, or a random sample beyond a cap. */
  considered: number;
  /** Token → contacts among `considered` with a value. */
  filled: Record<string, number>;
}

export interface PreviewRequest {
  contact_id: number;
  step_id?: number | null;
  subject_template: string;
  body_template: string;
  ai_variables: AiVariableInput[];
  generate_ai?: boolean;
  /** Values generated earlier in this editor, shown again while typing. */
  ai_values?: Record<string, string>;
  position?: number | null;
}

export interface PreviewContact {
  id: number;
  full_name: string;
  title: string | null;
  email: string | null;
  company_id: number | null;
  company_name: string | null;
  hidden_details: string[];
}

export interface PreviewResponse {
  contact: PreviewContact;
  subject: string;
  body: string;
  variables: ResolvedVariable[];
  errors: RenderErrorRead[];
  ai_values: Record<string, string>;
  ai_errors: Record<string, string>;
  sendable: boolean;
}

export interface AiTestRequest {
  contact_id: number;
  key: string;
  prompt_template: string;
  model?: string | null;
}

export interface AiTestResponse {
  input_resolved: string;
  output: string | null;
  model: string | null;
  errors: RenderErrorRead[];
  error: string | null;
}

// ── contacts in campaigns ────────────────────────────────────────────────

/** What Prism has on record about an address: VERIFIED when a stored proof
 *  covers this exact address (the CFO workflow's Apollo check); NONE — "no
 *  verification on record" — blocks nothing. */
export interface EmailVerification {
  state: "VERIFIED" | "NONE";
  source: string | null;
  verified_at: string | null;
}

/** One thing between a lead and its emails, as the backend decides it:
 *  `blocking` ones keep it from being launched, the others are warnings. */
export interface LeadIssue {
  code: string;
  message: string;
  blocking: boolean;
}

export interface CampaignContact {
  id: number;
  campaign_id: number;
  campaign_name: string;
  project_id: number;
  project_slug: string;
  scope: EmailScope;
  contact_id: number;
  contact_name: string;
  contact_title: string | null;
  email: string | null;
  hidden_details: string[];
  company_id: number | null;
  company_name: string | null;
  business_status: string | null;
  business_status_label: string | null;
  status: ContactEmailStatus;
  status_reason: string | null;
  current_step: number | null;
  steps_count: number;
  last_event_type: string | null;
  last_event_at: string | null;
  last_sent_at: string | null;
  last_opened_at: string | null;
  last_clicked_at: string | null;
  last_replied_at: string | null;
  added_at: string;
  updated_at: string;
  /** Handed to Lemlist (launched); null while still to launch. */
  queued_at: string | null;
  /** Its first email sent. */
  started_at: string | null;
  email_verification: EmailVerification;
  /** What stands between this lead and its emails (Leads list). */
  issues?: LeadIssue[];
  provider_lead_id: string | null;
  /** Lemlist's own state for the lead, as it said it. */
  provider_status: string | null;
  dry_run: boolean;
  provider_url: string | null;
}

export interface CampaignContactList {
  items: CampaignContact[];
  total: number;
  page: number;
  page_size: number;
  status_counts: Record<string, number>;
}

export interface CampaignEvent {
  id: number;
  type: string;
  provider_type: string;
  step_position: number | null;
  occurred_at: string;
  campaign_contact_id: number | null;
}

/** A reply: detected by its event; its text when Lemlist gave it. */
export interface EmailReply {
  id: number;
  step_position: number | null;
  received_at: string | null;
  subject: string | null;
  /** The new message, without the history it quotes (never HTML). */
  body_text: string | null;
  content_state: ContentState;
}

/** One step of one membership: what was (or will be) sent, and what came of it. */
export interface StepTimeline {
  position: number;
  step_id: number | null;
  step_name: string | null;
  delay_days: number | null;
  /** Not sent yet: the day it should go out — an estimate in the campaign's
   *  timezone, never an hour. null when it cannot be dated (schedule_hold). */
  planned_on: string | null;
  /** Estimated for a day already past and not reported sent. */
  planned_overdue?: boolean;
  /** null while Lemlist has not received this email. */
  status: MessageStatus | null;
  subject: string | null;
  body: string | null;
  ai_values: Record<string, string>;
  template_version: number | null;
  outdated: boolean;
  rendered_at: string | null;
  provider_synced_at: string | null;
  sent_at: string | null;
  open_count: number;
  click_count: number;
  first_opened_at: string | null;
  last_opened_at: string | null;
  last_clicked_at: string | null;
  replied_at: string | null;
  bounced_at: string | null;
  events: CampaignEvent[];
  replies: EmailReply[];
}

export interface ConversationItem {
  direction: "OUTBOUND" | "INBOUND";
  at: string;
  step_position: number | null;
  subject: string | null;
  body: string | null;
  status: MessageStatus | null;
  content_state: ContentState;
  open_count: number;
  click_count: number;
}

export interface CampaignContactDetail extends CampaignContact {
  steps: StepTimeline[];
  /** Why no next email can be dated: not launched, paused, ended, dry run. */
  schedule_hold?: string | null;
  conversation: ConversationItem[];
  /** Every Lemlist event of the membership, newest first. */
  events: CampaignEvent[];
}

export interface CompanyEmail {
  company_id: number;
  project_id: number;
  email_status: CompanyEmailStatus | null;
  business_status: string | null;
  business_status_label: string | null;
  memberships: CampaignContact[];
  /** In a legacy workflow sequence: no contact of it can join a campaign. */
  legacy_enrolled: boolean;
}

export interface ActionResultRow {
  id: number;
  ok: boolean;
  status: string | null;
  error: string | null;
  new_id: number | null;
}

export interface ActionResult {
  done: number;
  failed: number;
  rows: ActionResultRow[];
}

export interface ContactFilters {
  campaign_id?: number | null;
  status?: ContactEmailStatus[];
  business_status?: string[];
  step?: number | null;
  has_email?: boolean | null;
  added_after?: string | null;
  added_before?: string | null;
  include_removed?: boolean;
  /** true: handed to Lemlist; false: still to launch. */
  launched?: boolean | null;
  search?: string;
  page?: number;
  page_size?: number;
}

// ── add-contacts wizard ──────────────────────────────────────────────────

export interface CandidateContact {
  id: number;
  full_name: string;
  title: string | null;
  is_decision_maker: boolean;
  email: string | null;
  has_email: boolean;
  hidden_details: string[];
  company_id: number | null;
  company_name: string | null;
  project_id: number;
  project_slug: string;
  scope: EmailScope;
  business_status: string | null;
  business_status_label: string | null;
  email_status: ContactEmailStatus | null;
  current_campaign_id: number | null;
  current_campaign_name: string | null;
  /** The company is in a legacy workflow sequence: not addable. */
  legacy_enrolled: boolean;
  email_verification?: EmailVerification;
}

export interface CandidateCompany {
  id: number;
  name: string;
  domain: string | null;
  project_id: number;
  project_slug: string;
  scope: EmailScope;
  score: number | null;
  business_status: string | null;
  business_status_label: string | null;
  email_status: CompanyEmailStatus | null;
  contacts_count: number;
  eligible_contacts_count: number;
  email_ready_contacts_count: number;
  /** In a legacy workflow sequence: none of its contacts is addable. */
  legacy_enrolled: boolean;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface CandidateContactFilters {
  /** The Contacts page's filters. */
  company_id?: number | null;
  is_decision_maker?: boolean | null;
  enrichment_status?: string | null;
  /** The wizard's own. */
  business_status?: string[];
  role?: string;
  has_email?: boolean | null;
  /** A campaign id, or "none" for contacts in no campaign. */
  campaign_id?: number | "none" | null;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface CandidateCompanyFilters {
  /** The Companies page's list parameters, as its filter state builds them
   *  (q, filters, is_client, include_archived, archived_only, sort_by, order). */
  list: Record<string, unknown>;
  only_with_ready?: boolean;
  /** Only companies with a contact that has an email. */
  with_email?: boolean;
  page?: number;
  page_size?: number;
}

export interface AddContactsRequest {
  contact_ids: number[];
  company_ids: number[];
  move_existing: boolean;
  include_unsubscribed: boolean;
  exclude_contact_ids: number[];
}

export interface AddPreviewRow {
  contact_id: number;
  full_name: string;
  title: string | null;
  email: string | null;
  hidden_details: string[];
  company_id: number | null;
  company_name: string | null;
  current_campaign_id: number | null;
  current_campaign_name: string | null;
  eligibility: Eligibility;
  action: PlannedAction;
}

export interface AddPreviewSummary {
  selected: number;
  ready: number;
  no_email: number;
  already_in_campaign: number;
  in_other_campaign: number;
  legacy_enrolled: number;
  unsubscribed: number;
  bounced: number;
  invalid_email?: number;
  will_add: number;
  will_move: number;
  will_skip: number;
}

export interface AddPreviewResponse {
  campaign_id: number;
  summary: AddPreviewSummary;
  rows: AddPreviewRow[];
}

export interface AddContactsResponse {
  added: number;
  moved: number;
  skipped: number;
  rows: AddPreviewRow[];
}

// ── sync ─────────────────────────────────────────────────────────────────

export interface SyncResult {
  campaign_id: number;
  /** False = dry run: nothing left Prism. */
  external: boolean;
  campaign_created: boolean;
  steps_created: number;
  steps_updated: number;
  events_ingested: number;
  contacts_updated: number;
  updates_remaining: number;
  contacts_pushed: number;
  contacts_failed: number;
  remaining: number;
  daily_limit: number;
  pushed_today: number;
  /** Running contacts paused in Lemlist by this sync: their email for a new
   *  step is not ready, or they completed the sequence before it. */
  contacts_held: number;
  errors: string[];
  notes: string[];
  last_synced_at: string | null;
}

// ── launch ───────────────────────────────────────────────────────────────

/** One lead the launch refused or skipped, and why (in the backend's words). */
export interface LaunchFailure {
  membership_id: number;
  contact_name: string;
  reason: string;
}

/** A launch call: the sync, then READY leads handed over (the only way). */
export interface LaunchResult extends SyncResult {
  launched_ids: number[];
  failures: LaunchFailure[];
  /** Selected leads not to launch: launched already, ended, another campaign's. */
  skipped: LaunchFailure[];
  /** Lemlist refuses launches (its plan): no further call helps. */
  stopped: boolean;
}

export interface LaunchReadinessRow {
  lead: CampaignContact;
  ready: boolean;
  issues: LeadIssue[];
  steps_rendered: number;
  steps_total: number;
}

/** The Launch page, read without Lemlist and without a model. */
export interface LaunchReadiness {
  campaign_id: number;
  gates: LeadIssue[];
  can_launch: boolean;
  daily_limit: number;
  launched_today: number;
  remaining_today: number;
  to_launch: number;
  ready: number;
  blocked: number;
  launched: number;
  /** Every lead ready to launch, in launch order. */
  ready_ids: number[];
  items: LaunchReadinessRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface LaunchReadinessParams {
  search?: string;
  readiness?: "all" | "ready" | "blocked";
  page?: number;
  page_size?: number;
}

// ── performance ──────────────────────────────────────────────────────────

/** Leads — one person in this campaign — never messages. */
export interface LeadFunnel {
  launched: number;
  launched_dry_run: number;
  reached: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
}

/** Each ÷ the leads reached; null when none was reached (shown —). */
export interface PerformanceRates {
  open_rate: number | null;
  click_rate: number | null;
  reply_rate: number | null;
  bounce_rate: number | null;
  unsubscribe_rate: number | null;
}

/** Messages, never leads. */
export interface MessageTotals {
  sent: number;
  opened: number;
  opens: number;
  clicked: number;
  clicks: number;
  replies: number;
  bounced: number;
}

export interface StepPerformance extends PerformanceRates {
  /** null: replies and unsubscribes tied to no step. */
  position: number | null;
  name: string | null;
  entered: number;
  sent: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
}

export interface CampaignPerformance {
  campaign_id: number;
  timezone: string;
  launched_from: string | null;
  launched_to: string | null;
  /** Always now, whatever the period. */
  leads_in_campaign: number;
  leads_to_launch: number;
  funnel: LeadFunnel;
  rates: PerformanceRates;
  messages: MessageTotals;
  /** The cohort's leads by their state now. */
  states: Record<string, number>;
  steps: StepPerformance[];
  /** Day by day (campaign timezone), only the days with activity, oldest first. */
  timeline: PerformanceDay[];
}

/** One day of the cohort: distinct leads launched / emailed / opening /
 *  clicking / replying / bouncing / unsubscribing that day. */
export interface PerformanceDay {
  day: string;
  launched: number;
  sent: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
}

// ── outreach ─────────────────────────────────────────────────────────────

export type OutreachKind = "SENT" | "REPLIED" | "BOUNCED" | "UNSUBSCRIBED" | "SCHEDULED" | "OVERDUE";

export interface OutreachItem {
  kind: OutreachKind;
  /** An actual activity's day (viewer's timezone) or an estimate's (campaign's). */
  day: string;
  /** Actual time; null for an estimate (a day only, never an hour). */
  at: string | null;
  estimated: boolean;
  /** An overdue email's original estimate. */
  estimated_day: string | null;
  membership_id: number;
  contact_id: number;
  contact_name: string;
  contact_title: string | null;
  email: string | null;
  hidden_details: string[];
  company_id: number | null;
  company_name: string | null;
  campaign_id: number;
  campaign_name: string;
  project_slug: string;
  scope: EmailScope;
  step_position: number | null;
  step_name: string | null;
  status: ContactEmailStatus;
  sender: string | null;
}

export interface OutreachCampaign {
  id: number;
  name: string;
  status: CampaignStatus;
  sender: string | null;
}

export interface OutreachQueue {
  start: string;
  end: string;
  timezone: string;
  items: OutreachItem[];
  truncated: boolean;
  /** Leads not launched yet: no date until launched. */
  waiting_launch: number;
  /** Launched leads paused, or whose campaign is paused: no date. */
  on_hold: number;
  overdue: number;
  campaigns: OutreachCampaign[];
}

export interface OutreachParams {
  start: string;
  end: string;
  tz: string;
  campaign_id?: number | null;
  status?: ContactEmailStatus[];
  kinds?: OutreachKind[];
  sender?: string | null;
  search?: string;
}

// ── calls ────────────────────────────────────────────────────────────────

/** What went wrong, in the API's own words when it gave some. */
export function errorMessage(err: unknown, fallback = "Something went wrong."): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0] as { msg?: unknown; loc?: unknown };
      const where = Array.isArray(first.loc) ? String(first.loc[first.loc.length - 1]) : "";
      if (typeof first.msg === "string") {
        return where ? `${where.replace(/_/g, " ")}: ${first.msg.replace(/^Value error, /, "")}` : first.msg;
      }
    }
  }
  return fallback;
}

/** Drops empty values and joins lists the way the API reads them (`a,b`). */
export function queryParams(params: object): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length) out[key] = value.join(",");
      continue;
    }
    if (typeof value === "string" && value.trim() === "") continue;
    out[key] = value as string | number | boolean;
  }
  return out;
}

/** The project's Email API: the project is the path's, never a parameter. */
const email = (slug: string) => `/projects/${slug}/email`;

export async function getEmailMeta(slug: string): Promise<EmailMeta> {
  return (await apiClient.get<EmailMeta>(`${email(slug)}/meta`)).data;
}

export async function listCampaigns(
  slug: string,
  params: { status?: CampaignStatus | null } = {},
): Promise<Campaign[]> {
  return (await apiClient.get<Campaign[]>(`${email(slug)}/campaigns`, { params: queryParams(params) })).data;
}

export async function getVariableFill(slug: string, campaignId?: number | null): Promise<VariableFill> {
  return (
    await apiClient.get<VariableFill>(`${email(slug)}/variables/fill`, {
      params: { campaign_id: campaignId ?? undefined },
    })
  ).data;
}

export async function getSenders(slug: string): Promise<EmailSenders> {
  return (await apiClient.get<EmailSenders>(`${email(slug)}/senders`)).data;
}

export async function getCampaign(slug: string, id: number): Promise<CampaignDetail> {
  return (await apiClient.get<CampaignDetail>(`${email(slug)}/campaigns/${id}`)).data;
}

export async function createCampaign(slug: string, body: CampaignCreate): Promise<CampaignDetail> {
  return (await apiClient.post<CampaignDetail>(`${email(slug)}/campaigns`, body)).data;
}

export async function updateCampaign(slug: string, id: number, body: CampaignUpdate): Promise<CampaignDetail> {
  return (await apiClient.patch<CampaignDetail>(`${email(slug)}/campaigns/${id}`, body)).data;
}

export async function duplicateCampaign(slug: string, id: number): Promise<CampaignDetail> {
  return (await apiClient.post<CampaignDetail>(`${email(slug)}/campaigns/${id}/duplicate`)).data;
}

/** What deleting a campaign did: Lemlist keeps a live one, paused. */
export interface CampaignDeleteResult {
  id: number;
  name: string;
  provider_paused: boolean;
  note: string | null;
}

export async function deleteCampaign(slug: string, id: number): Promise<CampaignDeleteResult> {
  return (await apiClient.delete<CampaignDeleteResult>(`${email(slug)}/campaigns/${id}`)).data;
}

/** Sync / Refresh: the campaign, its events and the leads Lemlist already
 *  holds. Never hands a new lead over — that is `launchCampaign`. */
export async function syncCampaign(slug: string, id: number, limit?: number): Promise<SyncResult> {
  return (
    await apiClient.post<SyncResult>(`${email(slug)}/campaigns/${id}/sync`, limit ? { limit } : {})
  ).data;
}

export async function getOutreach(slug: string, params: OutreachParams): Promise<OutreachQueue> {
  return (await apiClient.get<OutreachQueue>(`${email(slug)}/outreach`, { params: queryParams(params) })).data;
}

export async function getCampaignPerformance(
  slug: string,
  id: number,
  params: { launched_from?: string | null; launched_to?: string | null } = {},
): Promise<CampaignPerformance> {
  return (
    await apiClient.get<CampaignPerformance>(`${email(slug)}/campaigns/${id}/performance`, {
      params: queryParams(params),
    })
  ).data;
}

export async function getLaunchReadiness(
  slug: string,
  id: number,
  params: LaunchReadinessParams = {},
): Promise<LaunchReadiness> {
  return (
    await apiClient.get<LaunchReadiness>(`${email(slug)}/campaigns/${id}/launch`, {
      params: queryParams(params),
    })
  ).data;
}

/** Launch: the selected leads (`membershipIds`), or the next eligible ones. */
export async function launchCampaign(
  slug: string,
  id: number,
  body: { membership_ids?: number[] | null; limit?: number } = {},
): Promise<LaunchResult> {
  return (await apiClient.post<LaunchResult>(`${email(slug)}/campaigns/${id}/launch`, body)).data;
}

export async function createStep(slug: string, campaignId: number, body: StepInput): Promise<CampaignDetail> {
  return (await apiClient.post<CampaignDetail>(`${email(slug)}/campaigns/${campaignId}/steps`, body)).data;
}

export async function getStepImpact(slug: string, campaignId: number, position: number | null): Promise<StepImpact> {
  return (
    await apiClient.get<StepImpact>(`${email(slug)}/campaigns/${campaignId}/steps/impact`, {
      params: position === null ? {} : { position },
    })
  ).data;
}

export async function reorderSteps(slug: string, campaignId: number, stepIds: number[]): Promise<CampaignDetail> {
  return (
    await apiClient.post<CampaignDetail>(`${email(slug)}/campaigns/${campaignId}/steps/reorder`, {
      step_ids: stepIds,
    })
  ).data;
}

export async function updateStep(slug: string, stepId: number, body: StepUpdate): Promise<CampaignStep> {
  return (await apiClient.patch<CampaignStep>(`${email(slug)}/steps/${stepId}`, body)).data;
}

export async function deleteStep(slug: string, stepId: number): Promise<CampaignDetail> {
  return (await apiClient.delete<CampaignDetail>(`${email(slug)}/steps/${stepId}`)).data;
}

export async function duplicateStep(slug: string, stepId: number): Promise<CampaignDetail> {
  return (await apiClient.post<CampaignDetail>(`${email(slug)}/steps/${stepId}/duplicate`)).data;
}

export async function createAiVariable(slug: string, stepId: number, body: AiVariableInput): Promise<CampaignStep> {
  return (await apiClient.post<CampaignStep>(`${email(slug)}/steps/${stepId}/ai-variables`, body)).data;
}

export async function updateAiVariable(
  slug: string,
  variableId: number,
  body: Partial<AiVariableInput>,
): Promise<CampaignStep> {
  return (await apiClient.patch<CampaignStep>(`${email(slug)}/ai-variables/${variableId}`, body)).data;
}

export async function deleteAiVariable(slug: string, variableId: number): Promise<CampaignStep> {
  return (await apiClient.delete<CampaignStep>(`${email(slug)}/ai-variables/${variableId}`)).data;
}

export async function testAiVariable(slug: string, body: AiTestRequest): Promise<AiTestResponse> {
  return (await apiClient.post<AiTestResponse>(`${email(slug)}/ai-variables/test`, body)).data;
}

export async function previewTemplate(slug: string, body: PreviewRequest): Promise<PreviewResponse> {
  return (await apiClient.post<PreviewResponse>(`${email(slug)}/templates/preview`, body)).data;
}

export async function listCampaignContacts(slug: string, filters: ContactFilters): Promise<CampaignContactList> {
  return (
    await apiClient.get<CampaignContactList>(`${email(slug)}/contacts`, { params: queryParams(filters) })
  ).data;
}

export async function getCampaignContact(slug: string, id: number): Promise<CampaignContactDetail> {
  return (await apiClient.get<CampaignContactDetail>(`${email(slug)}/campaign-contacts/${id}`)).data;
}

export type ContactAction = "pause" | "resume" | "remove";

export async function actOnContacts(slug: string, action: ContactAction, ids: number[]): Promise<ActionResult> {
  return (await apiClient.post<ActionResult>(`${email(slug)}/campaign-contacts/${action}`, { ids })).data;
}

export async function moveContacts(slug: string, ids: number[], targetCampaignId: number): Promise<ActionResult> {
  return (
    await apiClient.post<ActionResult>(`${email(slug)}/campaign-contacts/move`, {
      ids,
      target_campaign_id: targetCampaignId,
    })
  ).data;
}

export async function getCompanyEmail(slug: string, companyId: number): Promise<CompanyEmail> {
  return (await apiClient.get<CompanyEmail>(`${email(slug)}/companies/${companyId}`)).data;
}

export async function listCandidateContacts(
  slug: string,
  filters: CandidateContactFilters,
): Promise<Paged<CandidateContact>> {
  return (
    await apiClient.get<Paged<CandidateContact>>(`${email(slug)}/candidates/contacts`, {
      params: queryParams(filters),
    })
  ).data;
}

export async function listCandidateCompanies(
  slug: string,
  { list, ...filters }: CandidateCompanyFilters,
): Promise<Paged<CandidateCompany>> {
  return (
    await apiClient.get<Paged<CandidateCompany>>(`${email(slug)}/candidates/companies`, {
      params: queryParams({ ...list, ...filters }),
    })
  ).data;
}

export async function previewAddContacts(
  slug: string,
  campaignId: number,
  body: AddContactsRequest,
): Promise<AddPreviewResponse> {
  return (
    await apiClient.post<AddPreviewResponse>(`${email(slug)}/campaigns/${campaignId}/contacts/preview-add`, body)
  ).data;
}

export async function addContacts(
  slug: string,
  campaignId: number,
  body: AddContactsRequest,
): Promise<AddContactsResponse> {
  return (await apiClient.post<AddContactsResponse>(`${email(slug)}/campaigns/${campaignId}/contacts/add`, body))
    .data;
}

export async function syncPilotDraft(slug: string, campaignId: number): Promise<PilotDraftSync> {
  const result = await apiClient.post<PilotDraftSync>(
    `/projects/${slug}/email/campaigns/${campaignId}/sync-draft`,
  );
  return result.data;
}
